import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Show session expired message if redirected from expired token
  React.useEffect(() => {
    if (localStorage.getItem('sessionExpired') === 'true') {
      toast.error('Your session has expired. Please log in again.');
      localStorage.removeItem('sessionExpired');
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!username.trim() || !password.trim()) {
      toast.error('Please enter username and password');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: username.trim(),
        password: password.trim()
      });
      
      if (response.data.success) {
        // Store auth token
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('userName', response.data.username);
        localStorage.setItem('userEmail', response.data.email || '');
        toast.success(`Welcome, ${response.data.username}!`);
        onLogin(response.data.username);
      } else {
        toast.error(response.data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0e14] flex flex-col items-center justify-center p-4">
      {/* Header with Logo */}
      <div className="mb-12 text-center">
        <div className="flex items-center justify-center gap-4 mb-4">
          <h1 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold font-stark tracking-wider"
            style={{ 
              color: '#ff2d2d', 
              textShadow: '0 0 20px rgba(255, 45, 45, 0.7), 0 0 40px rgba(255, 45, 45, 0.5), 0 0 60px rgba(255, 45, 45, 0.3)' 
            }}
          >
            CARDIAC SOLUTIONS
          </h1>
          <motion.div
            animate={{
              scale: [1, 1.15, 1],
            }}
            transition={{
              repeat: Infinity,
              duration: 1.5,
              ease: "easeInOut"
            }}
            className="text-2xl sm:text-3xl lg:text-4xl"
            style={{ 
              color: '#ff2d2d',
              filter: 'drop-shadow(0 0 15px rgba(255, 45, 45, 0.8)) drop-shadow(0 0 30px rgba(255, 45, 45, 0.5))'
            }}
          >
            ❤️
          </motion.div>
        </div>
        <p className="text-cyan-400/70 text-sm uppercase tracking-widest font-stark">
          AED Assistant (AEDA)
        </p>
      </div>

      {/* Login Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-[#0d1520] border border-cyan-500/30 rounded-lg p-8 relative">
          {/* HUD corner accents */}
          <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-cyan-400/50"></div>
          <div className="absolute top-0 right-0 w-8 h-8 border-r-2 border-t-2 border-cyan-400/50"></div>
          <div className="absolute bottom-0 left-0 w-8 h-8 border-l-2 border-b-2 border-cyan-400/50"></div>
          <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-cyan-400/50"></div>

          <h2 className="text-xl font-stark text-cyan-400 mb-6 text-center uppercase tracking-wider">
            System Login
          </h2>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-cyan-300/80 text-sm mb-2 uppercase tracking-wider">
                Username
              </label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                className="w-full bg-[#0a0e14] border-cyan-500/30 text-cyan-100 placeholder:text-cyan-600/50 focus:border-cyan-400 focus:ring-cyan-400/30"
                autoComplete="username"
              />
            </div>

            <div>
              <label className="block text-cyan-300/80 text-sm mb-2 uppercase tracking-wider">
                Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="w-full bg-[#0a0e14] border-cyan-500/30 text-cyan-100 placeholder:text-cyan-600/50 focus:border-cyan-400 focus:ring-cyan-400/30 pr-10"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-cyan-500/50 hover:text-cyan-300 transition-colors"
                  data-testid="toggle-login-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-cyan-500 hover:bg-cyan-400 text-[#0a0e14] font-semibold py-6 text-lg uppercase tracking-wider glow-blue"
            >
              {isLoading ? (
                <motion.span
                  animate={{ opacity: [1, 0.5, 1] }}
                  transition={{ repeat: Infinity, duration: 1 }}
                >
                  Authenticating...
                </motion.span>
              ) : (
                'Login'
              )}
            </Button>
          </form>
        </div>

        {/* Security notice */}
        <p className="text-center text-cyan-500/40 text-xs mt-6 uppercase tracking-wider">
          Secure Access Only • Authorized Personnel
        </p>
      </motion.div>
    </div>
  );
};

export default Login;
